import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a 
              href="#labs" 
              className="text-sm font-medium text-gray-600 hover:text-[#0055FF] transition-colors"
              data-testid="nav-labs"
            >
              Labs
            </a>
            <a 
              href="#partners" 
              className="text-sm font-medium text-gray-600 hover:text-[#0055FF] transition-colors"
              data-testid="nav-partners"
            >
              Partners
            </a>
            <a 
              href="#contact" 
              className="text-sm font-medium text-gray-600 hover:text-[#0055FF] transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </a>
          </nav>
          <Button 
            asChild
            className="btn-electric px-6 py-2 text-sm font-medium hidden sm:flex"
            data-testid="header-cta"
          >
            <a href="#contact">Get in Touch</a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center pt-20 bg-white" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimatedSection>
            <div className="space-y-8">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500" data-testid="hero-overline">
                Innovation Hub
              </span>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#121212] leading-[0.95]" data-testid="hero-headline">
                Engineering the Future of Living.
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed font-light max-w-lg" data-testid="hero-subheadline">
                A boutique incubator dedicated to the art of the possible. Home to the next generation of AI-integrated lifestyle, culinary, and entertainment platforms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="btn-electric px-8 py-6 text-base font-medium inline-flex items-center gap-2"
                  data-testid="hero-cta-button"
                >
                  <a href="#labs">
                    Explore Our Labs
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection className="relative">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="https://images.unsplash.com/photo-1760629863094-5b1e8d1aae74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwcm9ib3QlMjB0ZWNobm9sb2d5JTIwbW9kZXJufGVufDB8fHx8MTc3NDkyNDE0OHww&ixlib=rb-4.1.0&q=85"
                alt="Futuristic robot technology"
                className="w-full aspect-[4/3] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent" />
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Portfolio Card Component
const PortfolioCard = ({ title, description, imageUrl, index, link }) => {
  const CardWrapper = link ? 'a' : 'div';
  const cardProps = link ? { href: link } : {};
  
  return (
    <motion.div variants={fadeInUp}>
      <CardWrapper
        {...cardProps}
        className={`portfolio-card bg-white border border-gray-200 overflow-hidden group block ${link ? 'cursor-pointer' : ''}`}
        data-testid={`portfolio-card-${index}`}
      >
        <div className="relative overflow-hidden">
          <motion.img
            src={imageUrl}
            alt={title}
            className="w-full aspect-[16/10] object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div className="thin-divider" />
        <div className="p-6 sm:p-8">
          <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-3">
            {title}
          </h3>
          <p className="text-gray-600 leading-relaxed font-light">
            {description}
          </p>
        </div>
      </CardWrapper>
    </motion.div>
  );
};

// Labs Section
const LabsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const labs = [
    {
      title: "LabrHood App",
      description: "The premier ecosystem for neighborhood synergy and modern community connection.",
      imageUrl: "/labrhood-app.png"
    },
    {
      title: "Vibr Labs",
      description: "Advanced metadata processing and AI vibe-matching for the entertainment industry.",
      imageUrl: "/vibr-labs.png"
    },
    {
      title: "Yes Chef AI",
      description: "A sophisticated real-time culinary assistant and automated inventory coach.",
      imageUrl: "/yeschef-ai.png"
    },
    {
      title: "AstroDots",
      description: "Advanced celestial resonance and bio-rhythm analysis platform using computer vision for iris and palm metrics.",
      imageUrl: "/astrodots.png",
      link: "/astrodots"
    }
  ];

  return (
    <section id="labs" className="py-24 sm:py-32 bg-white" data-testid="labs-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <AnimatedSection className="text-center mb-16 sm:mb-20">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
            The Labs
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#121212]" data-testid="labs-headline">
            Project Portfolio
          </h2>
        </AnimatedSection>
        
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {labs.map((lab, index) => (
            <PortfolioCard 
              key={index} 
              {...lab} 
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  const partnerLogos = [
    "https://images.unsplash.com/photo-1729830375022-b0248100c501?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBsb2dvJTIwZ3JheXNjYWxlfGVufDB8fHx8MTc3NDkyMTMwOXww&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1732997345946-700efbeb42e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBsb2dvJTIwZ3JheXNjYWxlfGVufDB8fHx8MTc3NDkyMTMwOXww&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1733259440156-6eee9771f6af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBsb2dvJTIwZ3JheXNjYWxlfGVufDB8fHx8MTc3NDkyMTMwOXww&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/4252898/pexels-photo-4252898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/29652324/pexels-photo-29652324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.unsplash.com/photo-1694878981750-3b1f8d97abff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBsb2dvJTIwZ3JheXNjYWxlfGVufDB8fHx8MTc3NDkyMTMwOXww&ixlib=rb-4.1.0&q=85"
  ];

  return (
    <section id="partners" className="py-24 sm:py-32 bg-[#F9FAFB]" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <AnimatedSection className="text-center mb-16 sm:mb-20">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
            Affiliates & Ventures
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#121212] mb-6" data-testid="partners-headline">
            Strategic Partnerships
          </h2>
          <p className="text-gray-600 text-lg font-light max-w-2xl mx-auto" data-testid="partners-description">
            Collaborating with industry leaders to bring AI utility to the masses.
          </p>
        </AnimatedSection>
        
        <AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {partnerLogos.map((logo, index) => (
              <motion.div
                key={index}
                className="flex items-center justify-center p-4"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <img
                  src={logo}
                  alt={`Partner ${index + 1}`}
                  className="partner-logo h-16 w-auto object-contain mix-blend-multiply"
                  data-testid={`partner-logo-${index}`}
                />
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Inquiry submitted successfully!", {
        description: "We'll get back to you soon."
      });
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry", {
        description: "Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <AnimatedSection>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Get in Touch
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#121212] mb-6" data-testid="contact-headline">
              Inquiries
            </h2>
            <p className="text-gray-600 text-lg font-light mb-8 max-w-md">
              Interested in collaboration or investment opportunities? We'd love to hear from you.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                For business development and partnership inquiries, please fill out the form and our team will respond within 24-48 hours.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-minimal w-full text-[#121212] placeholder:text-gray-400 border-0 border-b border-gray-300 rounded-none bg-transparent px-0 py-3 focus:ring-0 focus:border-[#0055FF] focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-minimal w-full text-[#121212] placeholder:text-gray-400 border-0 border-b border-gray-300 rounded-none bg-transparent px-0 py-3 focus:ring-0 focus:border-[#0055FF] focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="company"
                  placeholder="Company (Optional)"
                  value={formData.company}
                  onChange={handleChange}
                  className="input-minimal w-full text-[#121212] placeholder:text-gray-400 border-0 border-b border-gray-300 rounded-none bg-transparent px-0 py-3 focus:ring-0 focus:border-[#0055FF] focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="input-minimal w-full text-[#121212] placeholder:text-gray-400 border-0 border-b border-gray-300 rounded-none bg-transparent px-0 py-3 focus:ring-0 focus:border-[#0055FF] resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-electric w-full sm:w-auto px-8 py-6 text-base font-medium inline-flex items-center justify-center gap-2"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Inquiry
                    <Send className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500" data-testid="footer-copyright">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <HeroSection />
        <LabsSection />
        <PartnersSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
